import { useMemo, useSyncExternalStore, type CSSProperties } from "react";
import { useIsMobile } from "./use-mobile";

type Viewport = { height: number; width: number; top: number; left: number };
const fallback: Viewport = { height: 0, width: 0, top: 0, left: 0 };
let snapshot = fallback;
const listeners = new Set<() => void>();
let frame = 0;

function update() {
  const viewport = window.visualViewport;
  // Preserve browser pinch zoom instead of shrinking the UI as it zooms.
  if (viewport && viewport.scale !== 1) return;
  const next = {
    height: viewport?.height ?? window.innerHeight,
    width: viewport?.width ?? window.innerWidth,
    top: viewport?.offsetTop ?? 0,
    left: viewport?.offsetLeft ?? 0,
  };
  if (Object.keys(next).every(key => next[key as keyof Viewport] === snapshot[key as keyof Viewport])) return;
  snapshot = next;
  listeners.forEach(listener => listener());
}

function scheduleUpdate() {
  cancelAnimationFrame(frame);
  frame = requestAnimationFrame(update);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  if (listeners.size === 1) {
    update();
    window.addEventListener("resize", scheduleUpdate);
    window.visualViewport?.addEventListener("resize", scheduleUpdate);
    window.visualViewport?.addEventListener("scroll", scheduleUpdate);
  }
  return () => {
    listeners.delete(listener);
    if (!listeners.size) {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", scheduleUpdate);
      window.visualViewport?.removeEventListener("resize", scheduleUpdate);
      window.visualViewport?.removeEventListener("scroll", scheduleUpdate);
    }
  };
}

const getSnapshot = () => snapshot;
const getServerSnapshot = () => fallback;
const noSubscription = () => () => {};

/** One shared observer for browser bars, software keyboards and iOS panning. */
export function useOverlayViewport() {
  const isMobile = useIsMobile();
  const viewport = useSyncExternalStore(isMobile ? subscribe : noSubscription, getSnapshot, getServerSnapshot);
  const style = useMemo(() => viewport.height ? ({
    "--overlay-height": `${viewport.height}px`,
    "--overlay-width": `${viewport.width}px`,
    "--overlay-top": `${viewport.top}px`,
    "--overlay-left": `${viewport.left}px`,
  } as CSSProperties) : undefined, [viewport]);
  return { isMobile, style, height: viewport.height };
}
